import React, { createContext, useContext, useState, ReactNode } from 'react';

type Filters = {
  month: number | null; // compatibility: single-month filter (1-12) or null
  monthFrom: number | null; // 1-12
  monthTo: number | null; // 1-12
  year: number | null;
  setMonth: (m: number | null) => void;
  setMonthFrom: (m: number | null) => void;
  setMonthTo: (m: number | null) => void;
  setYear: (y: number | null) => void;
};

const DashboardFilterContext = createContext<Filters | undefined>(undefined);

export const DashboardFilterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [month, setMonth] = useState<number | null>(null);
  const [monthFrom, setMonthFromState] = useState<number | null>(null);
  const [monthTo, setMonthToState] = useState<number | null>(null);
  const [year, setYear] = useState<number | null>(null);

  // keep `month` in sync for backward compatibility: when only one month is selected
  const setMonthFrom = (m: number | null) => {
    setMonthFromState(m);
    // if only one side selected, set month to that value
    if (m !== null && monthTo === null) {
      setMonth(m);
    } else if (m === null && monthTo !== null) {
      setMonth(monthTo);
    } else if (m !== null && monthTo !== null) {
      // range selected -> clear single-month compatibility
      setMonth(null);
    } else {
      setMonth(null);
    }
  };

  const setMonthTo = (m: number | null) => {
    setMonthToState(m);
    if (m !== null && monthFrom === null) {
      setMonth(m);
    } else if (m === null && monthFrom !== null) {
      setMonth(monthFrom);
    } else if (m !== null && monthFrom !== null) {
      setMonth(null);
    } else {
      setMonth(null);
    }
  };

  return (
    <DashboardFilterContext.Provider
      value={{ month, monthFrom, monthTo, year, setMonth, setMonthFrom, setMonthTo, setYear }}
    >
      {children}
    </DashboardFilterContext.Provider>
  );
};

export const useDashboardFilters = () => {
  const ctx = useContext(DashboardFilterContext);
  if (!ctx) throw new Error('useDashboardFilters must be used within DashboardFilterProvider');
  return ctx;
};
